import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { getMainDefinition } from "@apollo/client/utilities";
import {
  split,
  ApolloClient,
  InMemoryCache,
  createHttpLink,
} from "@apollo/client";
import { createClient } from "graphql-ws";

import { clearSession, getSession } from "./App";

const wsLink = new GraphQLWsLink(
  createClient({
    url: process.env.REACT_APP_INC_API_SOCKET,
    connectionParams: {
      // authToken: user.authToken,
    },
  })
);

const httpLink = createHttpLink({
  uri: process.env.REACT_APP_INC_API,
  credentials: "same-origin",
});

const authLink = () =>
  setContext((_, { headers }) => {
    const session = getSession();

    return {
      headers: {
        ...headers,
        authorization: session?.token ? `Bearer ${session?.token}` : "",
      },
    };
  });

// The redeem mutation behind the lunch door scanner, and the route it runs on.
const LUNCH_REDEEM_OPERATION = "RedeemLunchPurchase";
const LUNCH_SCANNER_PATH = "/lunch/scanner";
const CONFERENCE_EXPORT_OPERATION = "ExportConferenceRegistrationsCsv";

// Whether a failed operation should send the whole SPA to the generic error
// page. A dropped fetch normally does, and /oh-no counts 15s before walking the
// user to /home.
//
// The lunch door is the exception. The scanner renders its own transport
// failure panel ("Sem conexão — tente novamente") and must keep the camera it
// has running, the code the volunteer typed, and the ?manual=1 flag the debug
// and e2e box rides on — navigating away throws the operator out of the queue
// and that panel can never render at all.
//
// Two ways in on purpose. The mutation is spared wherever it fires, because it
// is the door's own decision to handle. The scanner ROUTE is spared too,
// because the page is more than the mutation: ProtectedSuperAdminRoute and
// HeaderMenu each run the CurrentUser query with fetchPolicy
// "cache-and-network", so a drop on one of those would eject the operator just
// as effectively as a dropped scan.
export const shouldRedirectToErrorPage = ({
  operationName,
  pathname,
  networkError,
} = {}) => {
  if (networkError?.message !== "Failed to fetch") return false;
  if (operationName === LUNCH_REDEEM_OPERATION) return false;
  // The export action owns its error state and permits a complete retry.
  if (operationName === CONFERENCE_EXPORT_OPERATION) return false;
  if (pathname?.startsWith(LUNCH_SCANNER_PATH)) return false;
  return true;
};

const errorLink = onError(({ networkError, operation }) => {
  if (
    networkError?.statusCode === 401 &&
    operation?.operationName !== "Login"
  ) {
    clearSession();
    window.location.reload();
    return;
  }

  if (
    shouldRedirectToErrorPage({
      operationName: operation?.operationName,
      pathname: window.location.pathname,
      networkError,
    })
  ) {
    window.location.href = "/oh-no";
    return;
  }
});

const splitLink = split(
  ({ query }) => {
    const definition = getMainDefinition(query);
    return (
      definition.kind === "OperationDefinition" &&
      definition.operation === "subscription"
    );
  },
  wsLink,
  httpLink
);

export const buildClient = () =>
  new ApolloClient({
    link: authLink().concat(errorLink).concat(splitLink),
    cache: new InMemoryCache(),
  });
